<script>
/**
 * Recorrido de novedades: lo nuevo del panel, paso a paso y con una animación
 * que lo explica. Se abre solo encima del dashboard al entrar (una vez por
 * sesión) hasta que el usuario marca «No mostrar de nuevo», y siempre se puede
 * volver a abrir desde el botón de la barra superior
 * (this.$root.$emit('open-tour-novedades')).
 *
 * El «no mostrar» se guarda por usuario y por EDICIÓN del recorrido: cuando
 * haya novedades nuevas se sube EDICION y vuelve a salir a todos.
 */
const EDICION = "2026-10";
const PASOS = ["icons", "early", "reception", "translate", "voice"];

function leer(clave, almacen) {
  try { return window[almacen].getItem(clave); } catch (e) { return null; }
}
function guardar(clave, valor, almacen) {
  try { window[almacen].setItem(clave, valor); } catch (e) { /* sin almacenamiento: no pasa nada */ }
}

export default {
  name: "TourNovedades",
  data() {
    return {
      abierto: false,
      paso: 0,
      noMostrar: false,
      pasos: PASOS,
    };
  },
  computed: {
    usuarioId() {
      return (this.$store.state.userRoot && this.$store.state.userRoot.id) || "anon";
    },
    claveOculto() {
      return `wa_tour_novedades_${EDICION}_oculto_${this.usuarioId}`;
    },
    claveSesion() {
      return `wa_tour_novedades_${EDICION}_visto_${this.usuarioId}`;
    },
    clave() {
      return this.pasos[this.paso];
    },
    esUltimo() {
      return this.paso === this.pasos.length - 1;
    },
    enlace() {
      return {
        early: "/business/ai-agent",
        reception: "/business/ai-reception",
        translate: "/business/messages",
        voice: "/business/ai-agent",
      }[this.clave] || null;
    },
  },
  watch: {
    "$route.name": {
      immediate: true,
      handler() {
        this.quizaAbrir();
      },
    },
  },
  created() {
    this.$root.$on("open-tour-novedades", this.abrir);
  },
  beforeDestroy() {
    this.$root.$off("open-tour-novedades", this.abrir);
    document.removeEventListener("keydown", this.teclas);
  },
  methods: {
    /** Solo encima del dashboard, una vez por sesión y si no lo ha ocultado. */
    quizaAbrir() {
      if (this.abierto || !this.$route || this.$route.name !== "Dashboard") return;
      if (leer(this.claveOculto, "localStorage") === "1") return;
      if (leer(this.claveSesion, "sessionStorage") === "1") return;
      guardar(this.claveSesion, "1", "sessionStorage");
      // Un respiro para que el dashboard pinte primero y el recorrido entre encima.
      setTimeout(() => this.abrir(), 700);
    },
    abrir() {
      this.paso = 0;
      this.noMostrar = leer(this.claveOculto, "localStorage") === "1";
      this.abierto = true;
      document.addEventListener("keydown", this.teclas);
    },
    cerrar() {
      guardar(this.claveOculto, this.noMostrar ? "1" : "0", "localStorage");
      this.abierto = false;
      document.removeEventListener("keydown", this.teclas);
    },
    siguiente() {
      if (this.esUltimo) return this.cerrar();
      this.paso += 1;
    },
    anterior() {
      if (this.paso > 0) this.paso -= 1;
    },
    ir(i) {
      this.paso = i;
    },
    probar() {
      const destino = this.enlace;
      this.cerrar();
      if (destino && this.$route.path !== destino) this.$router.push(destino).catch(() => {});
    },
    teclas(e) {
      if (e.key === "Escape") this.cerrar();
      else if (e.key === "ArrowRight") this.siguiente();
      else if (e.key === "ArrowLeft") this.anterior();
    },
    t(k, p) {
      return this.$t("tourNovedades." + k, p);
    },
  },
};
</script>

<template>
  <transition name="tn-fade">
    <div v-if="abierto" class="tn" role="dialog" aria-modal="true" :aria-label="t('title')" @click.self="cerrar">
      <div class="tn__card">
        <button type="button" class="tn__x" :aria-label="t('close')" @click="cerrar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" /></svg>
        </button>

        <!-- La animación de cada paso. :key reinicia la animación al cambiar. -->
        <div class="tn__stage" :class="'tn__stage--' + clave" :key="'s' + clave">
          <!-- 1 · Iconos en las reservas -->
          <div v-if="clave === 'icons'" class="anim anim-icons">
            <div class="ai-row">
              <div class="ai-bar">
                <span class="ai-logo"></span>
                <span class="ai-name">María López</span>
                <span class="ai-ic ai-ic--1"><svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 6.6L21 11l-6.6 2.4L12 20l-2.4-6.6L3 11l6.6-2.4z" /></svg></span>
                <span class="ai-ic ai-ic--2"><svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.6a1 1 0 0 1-.25 1z" /></svg></span>
                <span class="ai-ic ai-ic--3"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M4 6h16M4 12h16M4 18h10" /></svg></span>
                <span class="ai-ic ai-ic--4"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /></svg></span>
              </div>
              <span class="ai-cursor"></span>
            </div>
            <div class="ai-tip">
              <div class="ai-tip__tit">{{ t("demo.iconsTipTitle") }}</div>
              <div class="ai-tip__line"><b>{{ t("demo.iconsTipEarly") }}</b> · 11:00 <i class="chip chip--ok">{{ t("demo.paid") }}</i></div>
              <div class="ai-tip__line ai-tip__line--muted">{{ t("demo.iconsTipPending") }} <b class="amber">120 €</b></div>
            </div>
          </div>

          <!-- 2 · Early access de pago -->
          <div v-else-if="clave === 'early'" class="anim anim-early">
            <div class="bub bub--in bub--1">{{ t("demo.earlyGuest") }}</div>
            <div class="bub bub--out bub--2">{{ t("demo.earlyAgent") }}<span class="paylink">💳 30 €</span></div>
            <div class="paid">
              <span class="paid__check"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="5 12 10 17 19 7" /></svg></span>
              {{ t("demo.earlyPaid") }}
            </div>
          </div>

          <!-- 3 · Recepción IA -->
          <div v-else-if="clave === 'reception'" class="anim anim-reception">
            <div v-for="(f, i) in ['early', 'late', 'extend']" :key="f" class="rx" :class="'rx--' + (i + 1)">
              <span class="rx__type" :class="'rx__type--' + f">{{ t("demo.rx." + f) }}</span>
              <span class="rx__who">{{ ['Lucía G.', 'Tom B.', 'Ana R.'][i] }}</span>
              <span class="rx__amt">{{ ['30 €', '25 €', '162 €'][i] }}</span>
              <span class="rx__st">
                <i class="st st--pend">{{ t("demo.pending") }}</i>
                <i class="st st--ok">{{ t("demo.confirmed") }}</i>
              </span>
            </div>
          </div>

          <!-- 4 · Traducción en tiempo real -->
          <div v-else-if="clave === 'translate'" class="anim anim-translate">
            <div class="tr-in">
              <span class="tr-flag">EN</span>
              <div class="tr-bub">
                <span class="tr-orig">{{ t("demo.trGuestOrig") }}</span>
                <span class="tr-mine">{{ t("demo.trGuestMine") }}</span>
              </div>
            </div>
            <div class="tr-out">
              <div class="tr-compose">
                <span class="tr-typed">{{ t("demo.trMine") }}</span>
                <span class="tr-preview"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 8l6 6M4 14l6-6 2-3M2 5h12M7 2h1M22 22l-5-10-5 10M14 18h6" /></svg>{{ t("demo.trTheirs") }}</span>
              </div>
              <span class="tr-key">Enter ↵</span>
            </div>
          </div>

          <!-- 5 · Llamadas de voz -->
          <div v-else-if="clave === 'voice'" class="anim anim-voice">
            <div class="vc-phone">
              <span class="vc-ring vc-ring--1"></span>
              <span class="vc-ring vc-ring--2"></span>
              <span class="vc-ico"><svg width="26" height="26" viewBox="0 0 24 24" fill="#fff"><path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.6a1 1 0 0 1-.25 1z" /></svg></span>
            </div>
            <div class="vc-wave"><i v-for="n in 9" :key="n" :style="{ animationDelay: (n * 0.09) + 's' }"></i></div>
            <div class="vc-sum">
              <div class="vc-sum__tit">{{ t("demo.voiceSummaryTitle") }}</div>
              <div class="vc-sum__txt">{{ t("demo.voiceSummary") }}</div>
            </div>
          </div>
        </div>

        <div class="tn__body">
          <div class="tn__kicker">{{ t("kicker") }} · {{ t("stepOf", { n: paso + 1, total: pasos.length }) }}</div>
          <h2 class="tn__title">{{ t("steps." + clave + ".title") }}</h2>
          <p class="tn__text">{{ t("steps." + clave + ".text") }}</p>
          <button v-if="enlace" type="button" class="tn__link" @click="probar">
            {{ t("steps." + clave + ".cta") }}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
          </button>
        </div>

        <div class="tn__foot">
          <label class="tn__check">
            <input v-model="noMostrar" type="checkbox" />
            <span>{{ t("dontShow") }}</span>
          </label>

          <div class="tn__dots" role="tablist">
            <button
              v-for="(p, i) in pasos"
              :key="p"
              type="button"
              class="tn__dot"
              :class="{ 'tn__dot--on': i === paso }"
              :aria-label="t('stepOf', { n: i + 1, total: pasos.length })"
              @click="ir(i)"
            ></button>
          </div>

          <div class="tn__nav">
            <button v-if="paso > 0" type="button" class="tn__btn tn__btn--ghost" @click="anterior">{{ t("prev") }}</button>
            <button type="button" class="tn__btn" @click="siguiente">{{ esUltimo ? t("done") : t("next") }}</button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.tn {
  --brand: #673de7;
  --brand-soft: #f3efff;
  --ink: #16131f;
  --muted: #6b6780;
  --line: #ece9f5;
  --ease: cubic-bezier(0.23, 1, 0.32, 1);
  position: fixed; inset: 0; z-index: 1300;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  background: rgba(22, 19, 31, 0.46);
  font-family: "DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.tn__card {
  position: relative; width: 100%; max-width: 620px; max-height: calc(100vh - 32px); overflow: auto;
  background: #fff; border-radius: 16px;
  box-shadow: 0 24px 64px rgba(22, 19, 31, 0.28);
  animation: tn-in 0.32s var(--ease);
}
@keyframes tn-in { from { opacity: 0; transform: translateY(10px) scale(0.98); } to { opacity: 1; transform: none; } }
.tn-fade-enter-active, .tn-fade-leave-active { transition: opacity 0.2s ease; }
.tn-fade-enter, .tn-fade-leave-to { opacity: 0; }

.tn__x {
  position: absolute; top: 12px; right: 12px; z-index: 2;
  width: 34px; height: 34px; border: 0; border-radius: 10px;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(255, 255, 255, 0.8); color: var(--muted); cursor: pointer;
}
.tn__x:hover { background: #fff; color: var(--ink); }
.tn__x:focus-visible, .tn__btn:focus-visible, .tn__link:focus-visible, .tn__dot:focus-visible { outline: 2px solid var(--brand); outline-offset: 2px; }

/* ── El escenario de la animación ── */
.tn__stage {
  position: relative; height: 250px; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(160deg, #f6f3ff 0%, #eef6ff 100%);
  border-bottom: 1px solid var(--line);
}
.tn__stage--early { background: linear-gradient(160deg, #eefaf2 0%, #f6f3ff 100%); }
.tn__stage--reception { background: linear-gradient(160deg, #f6f3ff 0%, #fff7ec 100%); }
.tn__stage--translate { background: linear-gradient(160deg, #eef6ff 0%, #f6f3ff 100%); }
.tn__stage--voice { background: linear-gradient(160deg, #f3efff 0%, #e9f4fb 100%); }
.anim { position: relative; width: 100%; max-width: 440px; padding: 0 20px; }

/* 1 · iconos */
.ai-row { position: relative; }
.ai-bar {
  display: flex; align-items: center; gap: 6px; height: 40px; padding: 0 10px 0 5px;
  border-radius: 999px; background: #3fae6a; color: #fff;
  animation: slide-in 0.5s var(--ease) both;
}
.ai-logo { width: 30px; height: 30px; border-radius: 50%; background: #fff; flex: none; }
.ai-name { flex: 1; font-weight: 700; font-size: 14px; }
.ai-ic {
  width: 22px; height: 22px; border-radius: 50%; background: #fff; flex: none;
  display: inline-flex; align-items: center; justify-content: center;
  opacity: 0; transform: scale(0.6); animation: pop 0.35s var(--ease) forwards;
}
.ai-ic--1 { color: #673de7; animation-delay: 0.6s; }
.ai-ic--2 { color: #0b6aa2; animation-delay: 0.8s; }
.ai-ic--3 { color: #b45309; animation-delay: 1s; }
.ai-ic--4 { color: #f59e0b; animation-delay: 1.2s; }
.ai-cursor {
  position: absolute; right: 18px; top: 26px; width: 14px; height: 14px;
  border-left: 2px solid var(--ink); border-top: 2px solid var(--ink); transform: rotate(-20deg);
  opacity: 0; animation: cursor 0.6s var(--ease) 1.5s forwards;
}
.ai-tip {
  margin: 14px 0 0 auto; width: 240px; padding: 10px 12px; border-radius: 12px; background: #fff;
  box-shadow: 0 10px 28px rgba(22, 19, 31, 0.12); font-size: 12.5px; color: #374151;
  opacity: 0; transform: translateY(6px); animation: rise 0.4s var(--ease) 1.9s forwards;
}
.ai-tip__tit { font-size: 10px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--brand); margin-bottom: 4px; }
.ai-tip__line { line-height: 1.6; }
.ai-tip__line--muted { color: var(--muted); }
.amber { color: #b45309; }
.chip { font-style: normal; font-size: 10.5px; font-weight: 700; padding: 1px 7px; border-radius: 999px; margin-left: 4px; }
.chip--ok { background: #ecfdf5; color: #047857; }

/* 2 · early de pago */
.anim-early { display: flex; flex-direction: column; gap: 10px; }
.bub {
  max-width: 78%; padding: 9px 12px; border-radius: 14px; font-size: 13px; line-height: 1.45;
  opacity: 0; transform: translateY(8px); animation: rise 0.4s var(--ease) forwards;
}
.bub--in { align-self: flex-start; background: #fff; color: var(--ink); border-bottom-left-radius: 4px; box-shadow: 0 4px 14px rgba(22, 19, 31, 0.06); }
.bub--out { align-self: flex-end; background: #dcf8c6; color: var(--ink); border-bottom-right-radius: 4px; }
.bub--1 { animation-delay: 0.2s; }
.bub--2 { animation-delay: 1s; }
.paylink { display: block; margin-top: 4px; font-weight: 700; color: #635bff; text-decoration: underline; }
.paid {
  align-self: center; display: inline-flex; align-items: center; gap: 8px; margin-top: 4px;
  padding: 7px 14px; border-radius: 999px; background: #fff; font-weight: 700; font-size: 13px; color: #15803d;
  box-shadow: 0 8px 22px rgba(21, 128, 61, 0.15);
  opacity: 0; transform: scale(0.95); animation: pop 0.4s var(--ease) 2s forwards;
}
.paid__check { width: 22px; height: 22px; border-radius: 50%; background: #16a34a; display: inline-flex; align-items: center; justify-content: center; }

/* 3 · recepción IA */
.anim-reception { display: flex; flex-direction: column; gap: 8px; }
.rx {
  display: grid; grid-template-columns: 112px 1fr auto 96px; align-items: center; gap: 10px;
  padding: 9px 12px; border-radius: 12px; background: #fff; font-size: 12.5px;
  box-shadow: 0 4px 14px rgba(22, 19, 31, 0.06);
  opacity: 0; transform: translateX(-10px); animation: slide-in 0.4s var(--ease) forwards;
}
.rx--1 { animation-delay: 0.15s; }
.rx--2 { animation-delay: 0.35s; }
.rx--3 { animation-delay: 0.55s; }
.rx__type { font-weight: 700; font-size: 11.5px; padding: 3px 8px; border-radius: 7px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rx__type--early { background: #ecfdf5; color: #047857; }
.rx__type--late { background: #fff7ed; color: #c2410c; }
.rx__type--extend { background: #eef2ff; color: #4338ca; }
.rx__who { font-weight: 700; color: var(--ink); }
.rx__amt { font-weight: 700; color: var(--ink); font-variant-numeric: tabular-nums; }
.rx__st { position: relative; height: 22px; }
.st { position: absolute; inset: 0; display: inline-flex; align-items: center; justify-content: center; font-style: normal; font-weight: 700; font-size: 11px; border-radius: 999px; }
.st--pend { background: #fef9c3; color: #854d0e; animation: fade-out 0.3s ease forwards; }
.st--ok { background: #ecfdf5; color: #047857; opacity: 0; animation: pop 0.35s var(--ease) forwards; }
.rx--1 .st--pend { animation-delay: 1.4s; } .rx--1 .st--ok { animation-delay: 1.5s; }
.rx--2 .st--pend { animation-delay: 1.9s; } .rx--2 .st--ok { animation-delay: 2s; }
.rx--3 .st--pend { animation-delay: 2.4s; } .rx--3 .st--ok { animation-delay: 2.5s; }

/* 4 · traducción */
.anim-translate { display: flex; flex-direction: column; gap: 14px; }
.tr-in { display: flex; align-items: flex-start; gap: 8px; }
.tr-flag { flex: none; font-size: 10px; font-weight: 800; color: #fff; background: #0b6aa2; border-radius: 6px; padding: 3px 6px; margin-top: 6px; }
.tr-bub { position: relative; display: grid; padding: 9px 12px; border-radius: 14px; border-bottom-left-radius: 4px; background: #fff; font-size: 13px; box-shadow: 0 4px 14px rgba(22, 19, 31, 0.06); }
.tr-orig, .tr-mine { grid-area: 1 / 1; }
.tr-orig { animation: fade-out 0.35s ease 1.2s forwards; }
.tr-mine { opacity: 0; animation: rise 0.35s var(--ease) 1.4s forwards; color: var(--ink); font-weight: 600; }
.tr-out { display: flex; align-items: flex-end; gap: 8px; justify-content: flex-end; }
.tr-compose { display: flex; flex-direction: column; gap: 6px; align-items: flex-end; max-width: 80%; }
.tr-typed {
  padding: 9px 12px; border-radius: 14px; background: #fff; border: 1.5px solid #d9cdfb; font-size: 13px; color: var(--ink);
  clip-path: inset(0 100% 0 0); animation: type 1s steps(24) 2s forwards;
}
.tr-preview {
  display: inline-flex; align-items: center; gap: 6px; padding: 7px 11px; border-radius: 12px;
  background: var(--brand-soft); color: #4c2bb3; font-size: 12.5px; font-weight: 600;
  opacity: 0; transform: translateY(6px); animation: rise 0.35s var(--ease) 3.3s forwards;
}
.tr-key {
  font-size: 11px; font-weight: 700; color: var(--muted); border: 1.5px solid var(--line); background: #fff; border-radius: 7px; padding: 3px 7px;
  opacity: 0; animation: key 0.6s var(--ease) 3.1s forwards;
}

/* 5 · voz */
.anim-voice { display: grid; grid-template-columns: auto 1fr; grid-template-rows: auto auto; gap: 12px 18px; align-items: center; }
.vc-phone { position: relative; grid-row: 1 / 3; width: 84px; height: 84px; display: flex; align-items: center; justify-content: center; }
.vc-ico { position: relative; z-index: 1; width: 60px; height: 60px; border-radius: 50%; background: var(--brand); display: flex; align-items: center; justify-content: center; animation: shake 1.4s ease-in-out 0.2s 2; }
.vc-ring { position: absolute; inset: 12px; border-radius: 50%; border: 2px solid var(--brand); opacity: 0; animation: ring 1.6s ease-out infinite; }
.vc-ring--2 { animation-delay: 0.8s; }
.vc-wave { display: flex; align-items: center; gap: 4px; height: 34px; }
.vc-wave i { width: 5px; height: 100%; border-radius: 3px; background: #8d6cf0; transform: scaleY(0.3); animation: wave 0.9s ease-in-out infinite; }
.vc-sum {
  padding: 10px 12px; border-radius: 12px; background: #fff; font-size: 12.5px; color: #374151; box-shadow: 0 8px 22px rgba(22, 19, 31, 0.08);
  opacity: 0; transform: translateY(6px); animation: rise 0.4s var(--ease) 1.8s forwards;
}
.vc-sum__tit { font-size: 10px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #0b6aa2; margin-bottom: 3px; }

@keyframes slide-in { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: none; } }
@keyframes pop { from { opacity: 0; transform: scale(0.6); } to { opacity: 1; transform: scale(1); } }
@keyframes rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes fade-out { to { opacity: 0; } }
@keyframes cursor { from { opacity: 0; transform: translate(12px, 12px) rotate(-20deg); } to { opacity: 1; transform: rotate(-20deg); } }
@keyframes type { to { clip-path: inset(0 0 0 0); } }
@keyframes key { 0% { opacity: 0; } 40% { opacity: 1; transform: translateY(2px); } 100% { opacity: 1; transform: none; } }
@keyframes ring { from { opacity: 0.7; transform: scale(1); } to { opacity: 0; transform: scale(1.7); } }
@keyframes wave { 0%, 100% { transform: scaleY(0.3); } 50% { transform: scaleY(1); } }
@keyframes shake { 0%, 100% { transform: rotate(0); } 10%, 30% { transform: rotate(-12deg); } 20%, 40% { transform: rotate(12deg); } 50% { transform: rotate(0); } }

/* ── Texto ── */
.tn__body { padding: 20px 24px 6px; }
.tn__kicker { font-size: 12px; font-weight: 700; color: var(--brand); }
.tn__title { margin: 6px 0 8px; font-size: 22px; line-height: 1.2; font-weight: 700; color: var(--ink); letter-spacing: -0.01em; text-wrap: balance; }
.tn__text { margin: 0; font-size: 14.5px; line-height: 1.55; color: #3f3b4f; text-wrap: pretty; }
.tn__link {
  margin-top: 10px; display: inline-flex; align-items: center; gap: 6px; padding: 0; border: 0; background: none;
  font-size: 14px; font-weight: 700; color: var(--brand); cursor: pointer;
}
.tn__link:hover { text-decoration: underline; }

/* ── Pie ── */
.tn__foot {
  display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 12px;
  padding: 16px 24px 20px;
}
.tn__check { display: inline-flex; align-items: center; gap: 8px; margin: 0; font-size: 13px; color: var(--muted); cursor: pointer; user-select: none; }
.tn__check input { width: 16px; height: 16px; accent-color: var(--brand); cursor: pointer; }
.tn__dots { display: flex; gap: 6px; }
.tn__dot { width: 8px; height: 8px; padding: 0; border: 0; border-radius: 999px; background: #dcd6ee; cursor: pointer; transition: width 0.2s var(--ease), background 0.2s; }
.tn__dot--on { width: 22px; background: var(--brand); }
.tn__nav { display: flex; justify-content: flex-end; gap: 8px; }
.tn__btn {
  height: 40px; padding: 0 18px; border: 0; border-radius: 10px; background: var(--brand); color: #fff;
  font-size: 14px; font-weight: 700; cursor: pointer; transition: transform 0.12s var(--ease), background 0.15s;
}
.tn__btn:hover { background: #5a32d6; }
.tn__btn:active { transform: scale(0.97); }
.tn__btn--ghost { background: #f3f1f9; color: var(--ink); }
.tn__btn--ghost:hover { background: #ebe7f6; }

@media (max-width: 560px) {
  .tn__stage { height: 220px; }
  .tn__body { padding: 16px 18px 4px; }
  .tn__title { font-size: 19px; }
  .tn__foot { grid-template-columns: 1fr auto; padding: 14px 18px 18px; }
  .tn__dots { grid-column: 1 / -1; grid-row: 1; justify-content: center; }
  .rx { grid-template-columns: 96px 1fr 88px; }
  .rx__amt { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .tn__card, .anim *, .anim { animation: none !important; transition: none !important; }
  .ai-ic, .ai-cursor, .ai-tip, .bub, .paid, .rx, .st--ok, .tr-mine, .tr-preview, .tr-key, .vc-sum { opacity: 1 !important; transform: none !important; }
  .tr-typed { clip-path: none !important; }
  .st--pend, .tr-orig { opacity: 0 !important; }
  .vc-wave i { transform: scaleY(0.6) !important; }
}
</style>
